import { Delete as DeleteIcon, Receipt as InvoiceIcon } from "@mui/icons-material";
import { Chip, Stack } from "@mui/material";
import type { FC } from "react";
import { Fragment, useState } from "react";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import MagicoInvoiceChip from "../../sharedComponents/invoices/MagicoInvoiceChip";
import type { OrderInvoice, SavedOrder } from "../orderTypes";
import { useOrderInvoices } from "./useOrderInvoices";

interface InvoiceChipProps {
  readonly filename: string;
  readonly url: string;
  readonly canDelete: boolean;
  readonly onDelete: () => void | Promise<void>;
}

const InvoiceChip: FC<InvoiceChipProps> = ({ filename, url, canDelete, onDelete }) => (
  <Chip
    label={filename}
    variant="outlined"
    color="secondary"
    onClick={(e) => {
      e.stopPropagation();
      return window.open(url, "_blank");
    }}
    icon={<InvoiceIcon />}
    deleteIcon={<DeleteIcon />}
    onDelete={canDelete ? onDelete : undefined}
  />
);

interface Props {
  readonly order: Pick<SavedOrder, "id" | "start" | "invoices">;
  readonly readonly?: boolean;
}

const OrderInvoices: FC<Props> = ({ order, readonly }) => {
  const { deleteInvoice } = useOrderInvoices(order);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    content: string;
    onConfirm: () => Promise<void>;
  }>();

  const handleDelete = async (invoice: OrderInvoice) => {
    setConfirmationDialog({
      content: `Usunąć fakturę '${invoice.name}'?`,
      onConfirm: async () => {
        await deleteInvoice(invoice.id);
        setConfirmationDialog(undefined);
      },
    });
  };
  if (order.invoices.length === 0) {
    return null;
  }
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        flexWrap: "wrap",
      }}
    >
      {order.invoices.map((invoice) => (
        <Fragment key={invoice.name}>
          <InvoiceChip
            filename={invoice.name}
            url={invoice.url}
            canDelete={!readonly}
            onDelete={() => handleDelete(invoice)}
          />
          {invoice.magico && <MagicoInvoiceChip twin={invoice.magico} />}
        </Fragment>
      ))}
      <ConfirmationDialog
        open={!!confirmationDialog}
        title="Usuń fakturę"
        content={confirmationDialog?.content}
        onCancel={() => setConfirmationDialog(undefined)}
        onConfirm={confirmationDialog?.onConfirm}
      />
    </Stack>
  );
};

export default OrderInvoices;
